import { useState } from 'react';
import { Input } from '@/components/ui/input';

export function DateInput({ value, onChange, language }) {
  return (
    <Input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid="answer-input"
      className="cyber-input w-full h-14 rounded-md px-4 text-base"
      autoFocus
    />
  );
}

export function NumberInput({ value, onChange, onKeyDown, language }) {
  return (
    <Input
      type="number"
      min="0"
      max="120"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={language === 'it' ? 'Inserisci un numero...' : 'Enter a number...'}
      data-testid="answer-input"
      className="cyber-input w-full h-14 rounded-md px-4 text-base"
      autoFocus
    />
  );
}

export function PlateInput({ value, onChange, onKeyDown, language }) {
  return (
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value.toUpperCase())}
      onKeyDown={onKeyDown}
      placeholder={language === 'it' ? 'Es: AB123CD' : 'Ex: AB123CD'}
      data-testid="answer-input"
      className="cyber-input w-full h-14 rounded-md px-4 text-base uppercase tracking-widest"
      autoFocus
      maxLength={10}
    />
  );
}

const VEHICLE_LABELS = {
  it: { targa: 'Targa', marca: 'Marca', modello: 'Modello', colore: 'Colore' },
  en: { targa: 'Plate', marca: 'Make', modello: 'Model', colore: 'Color' }
};

const parseVehicle = (val) => {
  const fields = { marca: '', modello: '', colore: '', targa: '' };
  if (!val || typeof val !== 'string') return fields;
  const byLabel = {};
  Object.values(VEHICLE_LABELS).forEach((l) => Object.entries(l).forEach(([k, v]) => { byLabel[v] = k; }));
  val.split(' - ').forEach((part) => {
    const i = part.indexOf(': ');
    const key = i > 0 ? byLabel[part.slice(0, i)] : null;
    if (key) fields[key] = part.slice(i + 2);
  });
  return fields;
};

const VehicleField = ({ label, testId, ...props }) => (
  <div>
    <label className="font-mono text-xs text-gray-400 block mb-1">{label}</label>
    <Input type="text" data-testid={testId} className="cyber-input w-full h-12 rounded-md px-3 text-sm" {...props} />
  </div>
);

export function VehicleInput({ value, onChange, language, mode = 'full', isRequired = false }) {
  const isBicycle = mode === 'bicycle';
  const it = language === 'it';
  const labels = VEHICLE_LABELS[it ? 'it' : 'en'];
  const opt = it ? ' (opzionale)' : ' (optional)';
  const [fields, setFields] = useState(() => parseVehicle(value));
  const keys = isBicycle ? ['marca', 'colore'] : ['targa', 'marca', 'modello', 'colore'];

  const updateField = (field, fieldValue) => {
    const updated = { ...fields, [field]: fieldValue };
    setFields(updated);
    onChange(keys.filter((k) => updated[k].trim()).map((k) => `${labels[k]}: ${updated[k].trim()}`).join(' - '));
  };

  const field = (key, label, placeholder, extra = {}) => (
    <VehicleField label={label} testId={`vehicle-${{ targa: 'plate', marca: 'make', modello: 'model', colore: 'color' }[key]}`}
      value={fields[key]} onChange={(e) => updateField(key, e.target.value)} placeholder={placeholder} {...extra} />
  );

  if (isBicycle) {
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="vehicle-input">
        {field('marca', labels.marca, it ? 'Es: Bianchi' : 'Ex: Bianchi', { autoFocus: true })}
        {field('colore', labels.colore, it ? 'Es: Nero' : 'Ex: Black')}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="vehicle-input">
      {field('targa', `${labels.targa}${isRequired ? ' *' : ''}`, it ? 'Es: AB123CD' : 'Ex: AB123CD', {
        autoFocus: true,
        onChange: (e) => updateField('targa', e.target.value.toUpperCase()),
        className: 'cyber-input w-full h-12 rounded-md px-3 text-sm uppercase tracking-wider'
      })}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {field('marca', labels.marca + opt, it ? 'Es: Fiat' : 'Ex: Fiat')}
        {field('modello', labels.modello + opt, it ? 'Es: Panda' : 'Ex: Panda')}
        {field('colore', labels.colore + opt, it ? 'Es: Bianco' : 'Ex: White')}
      </div>
    </div>
  );
}

export function EmailInput({ value, onChange, onKeyDown }) {
  return (
    <Input
      type="email"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder="email@esempio.com"
      data-testid="answer-input"
      className="cyber-input w-full h-14 rounded-md px-4 text-base"
      autoFocus
    />
  );
}

export function PhoneInput({ value, onChange, onKeyDown, language }) {
  return (
    <Input
      type="tel"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={language === 'it' ? 'Es: +39 333 1234567' : 'Ex: +39 333 1234567'}
      data-testid="answer-input"
      className="cyber-input w-full h-14 rounded-md px-4 text-base"
      autoFocus
    />
  );
}
